import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";

/**
 * Oznámení — `/admin/api/v1/notifications` (admin API, JWT cookie, ROLE_ADMIN,
 * feature `admin.notifications`).
 *
 * Server posílá **strojový `type` + `params`**, ne hotovou větu: text se skládá
 * tady v jazyce operátora (`notifications.type.*`). Anglické `title` / `message`
 * jsou jen záloha pro logy a případné budoucí odchozí doručení — kdyby přišel typ,
 * ke kterému ještě není překlad, vypíše se `title`.
 */
export type NotificationSeverity = "INFO" | "WARNING" | "ERROR";

export type NotificationCategory = "APPLICATION_ERROR" | "MAINTENANCE" | "SECURITY" | "EXPIRY" | "TEST";

export const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  "APPLICATION_ERROR",
  "MAINTENANCE",
  "SECURITY",
  "EXPIRY",
  "TEST",
];

export interface NotificationItem {
  id: number;
  category: NotificationCategory;
  /** Typ z uzavřeného výčtu na serveru (např. `MAINTENANCE_BACKUP_COMPLETED`). */
  type: string;
  severity: NotificationSeverity;
  /** Anglická záloha titulku. */
  title: string;
  message: string | null;
  /** Hodnoty do lokalizované věty (počty, jména, dny). */
  params: Record<string, unknown>;
  link: string | null;
  /** Kolikrát táž událost padla do tohoto řádku (slučování). */
  occurrences: number;
  /** Naivní čas v zóně serveru — renderovat přes `lib/format.ts`. */
  createdAt: string;
  lastSeenAt: string;
  readAt: string | null;
}

export interface NotificationPage {
  items: NotificationItem[];
  /** Nepřečtené celkem (napříč stránkami) — odznak zvonečku. */
  unreadCount: number;
  totalItems: number;
}

export interface NotificationQuery {
  unreadOnly?: boolean;
  page?: number;
  size?: number;
}

const KEY = "notifications";

export function useNotifications({ unreadOnly = false, page = 0, size = 10 }: NotificationQuery = {}) {
  return useQuery({
    queryKey: [KEY, { unreadOnly, page, size }],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      if (unreadOnly) params.set("unreadOnly", "true");
      return apiFetch<NotificationPage>(`/admin/api/v1/notifications?${params}`);
    },
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<void>(`/admin/api/v1/notifications/${id}/read`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

/**
 * Ručně vyvolané testovací oznámení — ověření, že kanál i zvoneček fungují, bez čekání
 * na skutečnou událost. Padá do vlastní kategorie `TEST`, takže se nikdy netváří jako
 * skutečná událost a jde vypnout zvlášť.
 */
export function useCreateTestNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (severity: NotificationSeverity) =>
      apiFetch<void>(`/admin/api/v1/notifications/test?severity=${severity}`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ markedRead: number }>("/admin/api/v1/notifications/read-all", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

/* ------------------------------ Nastavení kanálu ----------------------------- */

/**
 * Nastavení se veze na obecném endpointu kategorií (`/admin/api/v1/settings/notifications`),
 * takže sdílí ukládání i „Reset na výchozí" se zbytkem nastavení a nepotřebuje vlastní API.
 */
export interface NotificationCategorySettings {
  enabled: boolean;
  minSeverity: NotificationSeverity;
}

export interface NotificationSettingsConfig {
  retentionDays: number;
  dedupWindowMinutes: number;
  categories: Record<string, NotificationCategorySettings>;
}

interface SettingsEnvelope {
  category: string;
  config: NotificationSettingsConfig;
}

const SETTINGS_KEY = ["settings", "notifications"];

export function useNotificationSettings() {
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => apiFetch<SettingsEnvelope>("/admin/api/v1/settings/notifications"),
  });
}

export function useUpdateNotificationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (config: NotificationSettingsConfig) =>
      apiFetch<SettingsEnvelope>("/admin/api/v1/settings/notifications", {
        method: "PUT",
        json: { config },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SETTINGS_KEY });
      void qc.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

export function useResetNotificationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<SettingsEnvelope>("/admin/api/v1/settings/notifications/reset", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: SETTINGS_KEY }),
  });
}

/* --------------------------------- Vykreslení -------------------------------- */

/**
 * i18n klíč věty pro daný typ. Oddělené od komponent, aby šlo otestovat, že se
 * pro každý typ ze serveru hledá klíč, který ve `locales/*.json` existuje.
 */
export function notificationTitleKey(type: string): string {
  return `notifications.type.${type}.title`;
}

export function notificationMessageKey(type: string): string {
  return `notifications.type.${type}.message`;
}
